<article class="dashboard-funnel-card">
  <header class="dashboard-funnel-card__header">
    <div class="dashboard-funnel-card__header-top">
      <div class="dashboard-funnel-card__title-wrap">
        <h3 class="dashboard-funnel-card__title">{{ title() }}</h3>
        <button
          type="button"
          class="dashboard-funnel-card__info"
          [attr.title]="infoText()"
          [attr.aria-label]="infoText()"
        >
          <app-admin-icon name="help-circle" [size]="16" />
        </button>
      </div>

      <div class="dashboard-funnel-card__filters">
        <select
          class="dashboard-funnel-card__select cg-select"
          [ngModel]="dateRange()"
          (ngModelChange)="onDateRangeChange($event)"
          name="dateRange"
          aria-label="Date range"
        >
          <option value="last30Days">Last 30 Days</option>
          <option value="today">Today</option>
          <option value="thisWeek">This Week</option>
          <option value="thisMonth">This Month</option>
          <option value="custom">Custom</option>
          <option value="all">All Time</option>
        </select>

        <select
          class="dashboard-funnel-card__select cg-select"
          [ngModel]="ownerUserId()"
          (ngModelChange)="onOwnerChange($event)"
          name="ownerUserId"
          aria-label="Owner filter"
        >
          <option value="">All Owners</option>
          <option value="me">Assigned to Me</option>
          @for (owner of owners(); track owner.userId) {
            <option [value]="owner.userId">{{ owner.name }}</option>
          }
        </select>
      </div>
    </div>

    @if (dateRange() === 'custom') {
      <div class="dashboard-funnel-card__custom-dates">
        <label class="cg-admin-field cg-admin-field--compact dashboard-funnel-card__date-field">
          <span class="cg-admin-field__label">From</span>
          <input
            class="cg-input"
            type="date"
            [ngModel]="createdFrom()"
            (ngModelChange)="createdFrom.set($event); onCustomDateChange()"
            name="createdFrom"
          />
        </label>
        <label class="cg-admin-field cg-admin-field--compact dashboard-funnel-card__date-field">
          <span class="cg-admin-field__label">To</span>
          <input
            class="cg-input"
            type="date"
            [ngModel]="createdTo()"
            (ngModelChange)="createdTo.set($event); onCustomDateChange()"
            name="createdTo"
          />
        </label>
      </div>
    }

    @if (!loading() && stageViews().length > 0) {
      <div class="dashboard-funnel-card__metric">
        <span class="dashboard-funnel-card__metric-label">{{ totalLabel() }}</span>
        <strong class="dashboard-funnel-card__metric-value">{{ formatCount(totalCount()) }}</strong>
      </div>
    }
  </header>

  @if (loading()) {
    <div class="dashboard-funnel-card__loading">
      <app-admin-skeleton variant="card" />
      <app-admin-skeleton variant="text" />
      <app-admin-skeleton variant="text" />
    </div>
  } @else if (error()) {
    <div class="dashboard-funnel-card__body">
      <div class="cg-admin-alert cg-admin-alert--error">{{ error() }}</div>
    </div>
  } @else if (stageViews().length === 0) {
    <div class="dashboard-funnel-card__body">
      <app-admin-empty-state
        icon="bar-chart-3"
        title="No funnel data yet"
        description="Records matching your filters will appear here once statuses are configured."
      />
    </div>
  } @else {
    <div
      class="dashboard-funnel-card__body dashboard-funnel-card__content"
      [class.is-refreshing]="refreshing()"
    >
      <div class="dashboard-funnel-card__layout">
        <div class="dashboard-funnel-card__chart" aria-label="Funnel chart">
          @for (stage of stageViews(); track stage.statusId; let index = $index) {
            <div
              class="dashboard-funnel-card__segment"
              [class.is-empty]="stage.isEmpty"
            >
              <div
                class="dashboard-funnel-card__bar"
                tabindex="0"
                [style.background]="stageColor(index)"
                [style.clip-path]="segmentClipPath(stage.visualTopHalf, stage.visualBottomHalf)"
              >
                <div
                  class="dashboard-funnel-card__bar-copy"
                  [class.is-long-name]="stage.isLongName"
                >
                  <span class="dashboard-funnel-card__bar-name">{{ stage.name }}</span>
                  <span class="dashboard-funnel-card__bar-count">
                    {{ formatCountWithRate(stage.count, stage.displayConversionFromFirst) }}
                  </span>
                </div>

                <div class="dashboard-funnel-card__tooltip" role="tooltip">
                  @let tip = tooltipFor(stage);
                  <p class="dashboard-funnel-card__tooltip-title">{{ tip.stageName }}</p>
                  <dl class="dashboard-funnel-card__tooltip-list">
                    <div>
                      <dt>Count</dt>
                      <dd>{{ tip.count }}</dd>
                    </div>
                    <div>
                      <dt>% of Total</dt>
                      <dd>{{ tip.percentOfTotal }}</dd>
                    </div>
                    <div>
                      <dt>Previous Stage</dt>
                      <dd>{{ tip.previousConversion }}</dd>
                    </div>
                    <div>
                      <dt>Overall</dt>
                      <dd>{{ tip.overallConversion }}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          }
        </div>

        <div class="dashboard-funnel-card__table-wrap">
          <table class="dashboard-funnel-card__table">
            <colgroup>
              <col class="dashboard-funnel-card__col-stage" />
              <col class="dashboard-funnel-card__col-count" />
              <col class="dashboard-funnel-card__col-conversion" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">Stage</th>
                <th scope="col">Count</th>
                <th scope="col">Conversion %</th>
              </tr>
            </thead>
            <tbody>
              @for (stage of stageViews(); track stage.statusId; let index = $index) {
                <tr [class.is-empty]="stage.isEmpty">
                  <td>
                    <span class="dashboard-funnel-card__stage-cell">
                      <span
                        class="dashboard-funnel-card__stage-dot"
                        [style.background]="stageColor(index)"
                      ></span>
                      <span
                        class="dashboard-funnel-card__stage-name"
                        [class.is-long-name]="stage.isLongName"
                      >
                        {{ stage.name }}
                      </span>
                    </span>
                  </td>
                  <td class="dashboard-funnel-card__numeric">{{ formatCount(stage.count) }}</td>
                  <td class="dashboard-funnel-card__numeric">
                    <span class="dashboard-funnel-card__conversion-overall">
                      {{ formatRate(stage.conversionFromFirst) }}
                    </span>
                    @if (stage.conversionFromPrevious !== null) {
                      <span class="dashboard-funnel-card__conversion-step">
                        {{ formatRate(stage.conversionFromPrevious) }} step
                      </span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>

    @if (analytics(); as data) {
      <footer class="dashboard-funnel-card__footer">
        {{ footerLabel() }}: <strong>{{ formatRate(data.overallConversionRate) }}</strong>
      </footer>
    }
  }
</article>
